/**
 * Enhanced Video Manager
 * - Background videos: Play when in viewport, only one at a time
 * - Video-with-text: Play when in viewport, all can play simultaneously
 */

class EnhancedVideoManager {
  constructor() {
    this.backgroundVideos = new Map();
    this.regularVideos = new Map();
    this.init();
  }

  init() {
    // Disable the old VideoManager
    if (window.theme && window.theme.VideoManager) {
      window.theme.VideoManager.onSectionLoad = function() {};
      window.theme.VideoManager.onSectionUnload = function() {};
      console.log('Old VideoManager disabled, using Enhanced VideoManager');
    }

    // Setup videos when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAllVideos());
    } else {
      this.setupAllVideos();
    }

    // Handle dynamic sections
    document.addEventListener('shopify:section:load', (e) => {
      this.setupAllVideos();
    });
  }

  setupAllVideos() {
    const videoContainers = document.querySelectorAll('.video-container[data-video-url]');
    console.log(`Found ${videoContainers.length} video containers`);

    videoContainers.forEach((container, index) => {
      // Generate unique ID
      if (!container.dataset.videoId) {
        container.dataset.videoId = `video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      const videoId = container.dataset.videoId;
      const isBackground = container.classList.contains('video-container--background');
      const videoMap = isBackground ? this.backgroundVideos : this.regularVideos;

      // Skip if already setup
      if (videoMap.has(videoId)) {
        return;
      }

      const videoData = {
        id: videoId,
        container: container,
        url: container.dataset.videoUrl,
        autoplay: container.dataset.videoAutoplay === 'true',
        loop: container.dataset.videoLoop === 'true',
        type: this.getVideoType(container.dataset.videoUrl),
        isBackground: isBackground,
        player: null,
        isPlaying: false,
        observer: null
      };

      videoMap.set(videoId, videoData);

      console.log(`Setting up ${isBackground ? 'background' : 'regular'} video: ${videoId}`);

      // Initialize the video player
      this.initPlayer(videoData);
    });
  }

  getVideoType(url) {
    if (!url) return 'unknown';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    // Check for Shopify CDN videos or any MP4 files
    if (url.endsWith('.mp4') || url.includes('.mp4') || url.includes('cdn.shopify.com/videos')) return 'mp4';
    return 'unknown';
  }

  async initPlayer(videoData) {
    const videoElement = videoData.container.querySelector('.video-container__video');
    if (!videoElement) return;

    try {
      switch (videoData.type) {
        case 'vimeo':
          await this.initVimeo(videoData, videoElement);
          break;
        case 'youtube':
          await this.initYouTube(videoData, videoElement);
          break;
        case 'mp4':
          await this.initMP4(videoData, videoElement);
          break;
      }

      // Setup intersection observer for background videos only
      if (videoData.player) {
        if (videoData.isBackground) {
          this.setupBackgroundObserver(videoData);
        } else {
          // For regular videos, just play them after a short delay
          if (videoData.autoplay) {
            console.log(`Video-with-text ready: ${videoData.id}, will auto-play`);
            setTimeout(() => {
              console.log(`Playing video-with-text: ${videoData.id}`);
              this.playVideo(videoData);
            }, 1000); // 1 second delay to ensure player is ready
          } else {
            console.log(`Video-with-text ${videoData.id} has autoplay disabled`);
          }
        }
      } else {
        console.log(`No player created for ${videoData.id}`);
      }

      // Setup manual play button if exists
      const playButton = videoData.container.querySelector('.video-container__play');
      if (playButton) {
        playButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.playVideo(videoData);
        });
      }
    } catch (error) {
      console.error(`Failed to init player for ${videoData.id}:`, error);
    }
  }

  async initVimeo(videoData, videoElement) {
    // Load Vimeo API if needed
    if (!window.Vimeo) {
      await this.loadScript('https://player.vimeo.com/api/player.js');
      // Wait for it to be available
      let attempts = 0;
      while (!window.Vimeo && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
    }

    const playerId = `vimeo-${videoData.id}`;
    videoElement.innerHTML = `<div id="${playerId}"></div>`;

    const player = new Vimeo.Player(playerId, {
      url: videoData.url,
      autoplay: false,
      autopause: false,  // This is the key! Allow multiple videos to play
      muted: true,
      loop: videoData.loop,
      controls: false,
      title: false,
      byline: false,
      portrait: false,
      background: false
    });

    videoData.player = player;

    player.on('play', () => {
      videoData.isPlaying = true;
      videoData.container.classList.add('video-container--playing', 'video--play-started');

      // If this is a background video, pause other background videos
      if (videoData.isBackground) {
        this.pauseOtherBackgroundVideos(videoData.id);
      }
    });

    player.on('pause', () => {
      videoData.isPlaying = false;
      if (videoData.isBackground) {
        videoData.container.classList.remove('video-container--playing');
      }
    });

    player.on('ended', () => {
      videoData.isPlaying = false;
      // For looping videos, restart them
      if (videoData.loop && !videoData.isBackground) {
        setTimeout(() => this.playVideo(videoData), 100);
      }
    });

    await player.ready();

    // Position background videos
    if (videoData.isBackground) {
      this.positionBackgroundVideo(videoData, videoElement);
    }
  }

  async initYouTube(videoData, videoElement) {
    // Load YouTube API if needed
    if (!window.YT) {
      await this.loadScript('https://www.youtube.com/iframe_api');
      await new Promise((resolve) => {
        window.onYouTubeIframeAPIReady = resolve;
      });
    }

    const videoId = this.extractYouTubeId(videoData.url);
    const playerId = `youtube-${videoData.id}`;
    videoElement.innerHTML = `<div id="${playerId}"></div>`;

    return new Promise((resolve) => {
      const player = new YT.Player(playerId, {
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: videoData.isBackground ? 0 : 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          loop: videoData.loop ? 1 : 0,
          playlist: videoData.loop ? videoId : '',
          mute: 1
        },
        events: {
          onReady: () => {
            videoData.player = player;
            player.mute();

            // Position background videos
            if (videoData.isBackground) {
              this.positionBackgroundVideo(videoData, videoElement);
            }

            resolve();
          },
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              videoData.isPlaying = true;
              videoData.container.classList.add('video-container--playing', 'video--play-started');

              // If this is a background video, pause other background videos
              if (videoData.isBackground) {
                this.pauseOtherBackgroundVideos(videoData.id);
              }
            } else {
              videoData.isPlaying = false;
              videoData.container.classList.remove('video-container--playing');
            }
          }
        }
      });
    });
  }

  async initMP4(videoData, videoElement) {
    const video = document.createElement('video');
    video.src = videoData.url;
    video.playsInline = true;
    video.muted = true;
    video.loop = videoData.loop;
    video.controls = false;
    // Add autoplay attribute for better compatibility
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    // Preload the video for faster playback
    video.preload = 'auto';

    video.addEventListener('loadedmetadata', () => {
      console.log(`MP4 video metadata loaded: ${videoData.id}`);
    });

    video.addEventListener('canplay', () => {
      console.log(`MP4 video can play: ${videoData.id}`);
    });

    video.addEventListener('play', () => {
      videoData.isPlaying = true;
      videoData.container.classList.add('video-container--playing', 'video--play-started');

      // If this is a background video, pause other background videos
      if (videoData.isBackground) {
        this.pauseOtherBackgroundVideos(videoData.id);
      }
    });

    video.addEventListener('pause', () => {
      videoData.isPlaying = false;
      videoData.container.classList.remove('video-container--playing');
    });

    video.addEventListener('ended', () => {
      videoData.isPlaying = false;
      // For looping videos, restart them
      if (videoData.loop && !videoData.isBackground) {
        video.currentTime = 0;
        video.play().catch(e => console.log('Failed to loop video:', e));
      }
    });

    videoElement.innerHTML = '';
    videoElement.appendChild(video);
    videoData.player = video;
  }

  // Background videos - play when visible, pause when not, only one at a time
  setupBackgroundObserver(videoData) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
          // Video is visible
          if (videoData.autoplay && !videoData.isPlaying) {
            console.log(`Playing background video: ${videoData.id}`);
            this.playVideo(videoData);
          }
        } else if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
          // Video is not visible
          if (videoData.isPlaying) {
            console.log(`Pausing background video: ${videoData.id}`);
            this.pauseVideo(videoData);
          }
        }
      });
    }, {
      threshold: [0, 0.1, 0.25, 0.5, 1.0]
    });

    observer.observe(videoData.container);
    videoData.observer = observer;
  }


  pauseOtherBackgroundVideos(currentVideoId) {
    this.backgroundVideos.forEach((video, id) => {
      if (id !== currentVideoId && video.isPlaying) {
        console.log(`Pausing other background video: ${id}`);
        this.pauseVideo(video);
      }
    });
  }

  positionBackgroundVideo(videoData, videoElement) {
    const container = videoData.container;
    const iframe = videoElement.querySelector('iframe');
    if (!iframe) return;

    const positionVideo = () => {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const containerRatio = containerWidth / containerHeight;

      const iframeWidth = parseInt(iframe.getAttribute('width')) || 640;
      const iframeHeight = parseInt(iframe.getAttribute('height')) || 360;
      const videoRatio = iframeWidth / iframeHeight;

      const videoCrop = 75;

      if (containerRatio > videoRatio) {
        const newHeight = containerWidth / videoRatio + videoCrop * 2;
        videoElement.style.marginTop = ((containerHeight - newHeight) / 2 - videoCrop) + 'px';
        videoElement.style.marginLeft = '';
        videoElement.style.height = (newHeight + videoCrop * 2) + 'px';
        videoElement.style.width = '';
      } else {
        const newWidth = containerHeight * videoRatio + videoCrop * 2 * videoRatio;
        videoElement.style.marginTop = -videoCrop + 'px';
        videoElement.style.marginLeft = ((containerWidth - newWidth) / 2) + 'px';
        videoElement.style.height = (containerHeight + videoCrop * 2) + 'px';
        videoElement.style.width = newWidth + 'px';
      }
    };

    positionVideo();
    window.addEventListener('resize', positionVideo);
  }

  async playVideoWithRetry(videoData, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        await this.playVideo(videoData);
        if (videoData.isPlaying) {
          console.log(`Video ${videoData.id} is playing after ${i + 1} attempt(s)`);
          break;
        }
      } catch (error) {
        console.log(`Retry ${i + 1} failed for ${videoData.id}`);
      }

      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
      }
    }
  }

  async playVideo(videoData) {
    if (!videoData.player) {
      console.log(`No player for ${videoData.id}`);
      return;
    }

    console.log(`Attempting to play ${videoData.isBackground ? 'background' : 'regular'} video: ${videoData.id}`);

    try {
      switch (videoData.type) {
        case 'youtube':
          videoData.player.playVideo();
          break;
        case 'vimeo':
          await videoData.player.play();
          break;
        case 'mp4':
          // For MP4, ensure video is muted before playing (browser requirement)
          videoData.player.muted = true;

          // Try to play with a promise
          const playPromise = videoData.player.play();

          if (playPromise !== undefined) {
            await playPromise.catch(error => {
              console.warn(`Initial play attempt failed for ${videoData.id}, trying with user interaction simulation:`, error);

              // If autoplay was blocked, try clicking play programmatically
              videoData.player.muted = true;
              videoData.player.volume = 0;

              // Fallback: try playing after a short delay
              return new Promise((resolve) => {
                setTimeout(() => {
                  videoData.player.play().then(resolve).catch(e => {
                    console.error(`Second play attempt also failed for ${videoData.id}:`, e);
                    resolve(); // Don't throw, just log
                  });
                }, 100);
              });
            });
          }
          break;
      }
      console.log(`Successfully started ${videoData.id}`);
    } catch (error) {
      // Log all errors to see what's happening
      console.warn(`Play failed for ${videoData.id}:`, error.message);
      throw error; // Re-throw for retry logic
    }
  }

  pauseVideo(videoData) {
    if (!videoData.player || !videoData.isPlaying) return;

    try {
      switch (videoData.type) {
        case 'youtube':
          videoData.player.pauseVideo();
          break;
        case 'vimeo':
          videoData.player.pause();
          break;
        case 'mp4':
          videoData.player.pause();
          break;
      }
    } catch (error) {
      console.warn(`Pause failed for ${videoData.id}:`, error);
    }
  }

  extractYouTubeId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
}

// Initialize
if (typeof window.enhancedVideoManager === 'undefined') {
  window.enhancedVideoManager = new EnhancedVideoManager();
}